<template>
    <app-layout>
        <div class="flex flex-col">
            <page-header title="Downtime Summary">
                <div class="mt-3 flex items-start justify-between">
                    <filter-bar v-model="queryParams" :config="filterBarConfig" />
                </div>
            </page-header>

            <data-table :columns="fields" :items="downtime" :no-items-view="{ noItems: 'No data available' }" :query-params="queryParams"
                ><
                <template #type="{ item }">
                    <td :title="item.type.label" class="align-middle">
                        {{ item.type.shortLabel }}
                    </td>
                </template>
                <template #total_downtime="{ item }">
                    <td class="align-middle">
                        {{ formatSecondsToDDDHHMM(item.total_downtime) }}
                    </td>
                </template>
                <template #average_downtime="{ item }">
                    <td class="align-middle">
                        {{ formatSecondsToDDDHHMM(item.average_downtime) }}
                    </td>
                </template>

                <template #reasons="{ item }">
                    <td class="align-middle">
                        <ul v-if="item.reasons.length" class="list-unstyled">
                            <li v-for="reason in item.reasons" :key="reason">{{ reason }}</li>
                        </ul>
                        <span v-else>-</span>
                    </td>
                </template>

                <template #tickets="{ item }">
                    <td class="align-middle">
                        <ul v-if="item.tickets.length" class="list-unstyled m-0">
                            <li v-for="ticket in item.tickets" :key="ticket">
                                <a :href="route('workorders.show', ticket.id)" @click.prevent="showWorkorder(ticket.id)">{{ ticket.number }}</a>
                            </li>
                        </ul>
                        <span v-else>-</span>
                    </td>
                </template>
            </data-table>
        </div>
    </app-layout>
</template>
<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import FilterBar from '@/Components/FilterBar.vue';
import PageHeader from '@/Components/PageHeader.vue';
import { computed, ref } from 'vue';
import { trans } from '@/mixins/i18n.js';
import DataTable from '@/Components/DataTable.vue';
import { visitModal } from '@inertiaui/modal-vue';
import { formatSecondsToDDDHHMM } from '@/Utils/formatDuration';

const props = defineProps({
    downtime: Object,
    filters: Object,
});

const queryParams = ref(null);
const specificLocations = computed(() => (props.filters?.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));
const areas = computed(() => (props.filters?.areas?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));

const filterBarConfig = computed(() => ({
    route: route('reports.downtime-summary'),
    defaultSort: { column: 'total_downtime', asc: false },
    routeOptions: { only: ['downtime'] },
    filters: [
        { type: 'date-range', label: trans('activities.overview.cell.date'), startKey: 'start', endKey: 'end' },
        {
            type: 'multi-select',
            key: 'types',
            label: trans('assets.overview.filter.type'),
            options: props.filters?.types?.data ?? [],
        },
        {
            type: 'multi-select',
            key: 'specificLocations',
            label: trans('activities.overview.cell.specific_location'),
            options: specificLocations.value,
            showSearchbar: true,
        },
        {
            type: 'multi-select',
            key: 'areas',
            label: trans('locations.area.label'),
            options: areas.value,
            showSearchbar: true,
        },
    ],
}));

const fields = [
    { key: 'type', label: 'Type', sorter: false },
    { key: 'designation', label: 'Designation', sorter: false },
    { key: 'curie_id', label: 'Curie ID', sorter: false },
    { key: 'manufacturer', label: 'Vendor', sorter: false },
    { key: 'specific_location', label: 'Specific Location', sorter: false },
    { key: 'area', label: 'Area', sorter: false },
    { key: 'total_downtime', label: 'Total Downtime', sorter: false },
    { key: 'incidents', label: '# of Incidents', sorter: false },
    { key: 'average_downtime', label: 'Average Downtime', sorter: false },
    { key: 'reasons', label: 'Reasons for Failure', sorter: false },
    { key: 'tickets', label: 'Related Tickets', sorter: false },
];

function showWorkorder(id) {
    visitModal(route('workorders.show', id));
}
</script>
